import type { TempleLandmarkTemplate } from '../../../../shared/scene/ruinsPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';

function sanctum(p: TownPropPalette): string {
  const courses = [274, 342, 409, 478, 551, 612].map((y, i) => `
    <path d="M9 ${y}H62 M238 ${y - (i % 2) * 8}H291" stroke="${hex(p.shade)}" stroke-width="3"/>
    <path d="M${i % 2 ? 28 : 44} ${y + 4}v${i === 5 ? 26 : 56}
      M${i % 2 ? 257 : 276} ${y + 3}v${i === 5 ? 27 : 52}" stroke="${hex(p.shade)}" stroke-width="2"/>`).join('');
  const frieze = [42, 96, 150, 204, 258].map((x, i) => `
    <path d="M${x - 17} 60l17 -16 17 16 -17 16Z" fill="${hex(p.wood)}"/>
    <path d="M${x - 10} 60l10 -9 10 9 -10 9Z" fill="${hex(i === 2 ? p.woodEdge : p.stone)}"/>
    <path d="M${x - 4} 60h8" stroke="${hex(p.shade)}" stroke-width="2"/>`).join('');
  return `<g data-part="temple-sanctum">
    <path d="M0 0H300V660H232V244Q232 183 150 142Q68 183 68 244V660H0Z" fill="${hex(p.shade)}"/>
    <path d="M0 0H300V20H0Z M4 28H296V92H4Z" fill="${hex(p.primary)}"/>
    <path d="M0 20H300V28H0Z M4 88H296V102H4Z" fill="${hex(p.wood)}"/>
    <path d="M6 29H294M6 83H294" stroke="${hex(p.roofEdge)}" stroke-width="3"/>
    ${frieze}
    <path d="M6 109H145Q74 153 53 216L52 622H6Z
      M155 109H294V622H248L247 216Q226 153 155 109Z" fill="${hex(p.stone)}"/>
    <path d="M10 112H139Q73 156 48 217L46 620H13V257L9 247Z
      M161 112H288L279 152 288 174V620H257L255 216Q231 155 161 112Z" fill="${hex(p.primary)}"/>
    <path d="M63 240Q65 174 150 132Q235 174 237 240" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="6"/>
    <path d="M51 211l15 5 M72 169l13 10 M107 137l10 13 M183 150l10 -13 M215 179l13 -10 M234 217l15 -6"
      stroke="${hex(p.shade)}" stroke-width="4"/>
    ${courses}
    <path d="M15 306l15 -8 9 15 -8 24 M281 414l-15 11 6 17 -12 19
      M21 551l10 13 -5 30" fill="none" stroke="${hex(p.shade)}" stroke-width="3"/>
    <path d="M8 618H62V637H8Z M238 618H292V637H238Z" fill="${hex(p.roofEdge)}"/>
    <path d="M0 637H68V660H0Z M232 637H300V660H232Z" fill="${hex(p.stone)}"/>
    <path d="M4 639H64 M236 639H296" stroke="${hex(p.primary)}" stroke-width="3"/>
    <path d="M12 647l11 2 9 11H9Z M267 650l19 -7 10 17h-35Z" fill="${hex(p.shade)}"/>
  </g>`;
}

function relief(p: TownPropPalette): string {
  const rays = Array.from({ length: 12 }, (_, i) => {
    const a = i * Math.PI / 6, x = Math.cos(a), y = Math.sin(a);
    return `<path d="M${80 + x * 30} ${82 + y * 30}L${80 + x * 41} ${82 + y * 41}"/>`;
  }).join('');
  const inscriptions = [148, 169, 190].map((y, i) => `
    <path d="M33 ${y}h${i === 1 ? 21 : 32}m8 0h17m8 0h${i === 2 ? 16 : 28}
      M41 ${y + 6}v5m27 -5v5m27 -5v5m19 -5v5"/>`).join('');
  return `<g data-part="temple-relief">
    <path d="M22 7L118 0 140 16 149 235 160 244V260H0V244L13 236 11 30Z" fill="${hex(p.shade)}"/>
    <path d="M24 13L117 7 133 22 140 232H20L19 32Z" fill="${hex(p.primary)}"/>
    <path d="M32 28H121V215H30Z" fill="${hex(p.stone)}"/>
    <path d="M34 29H121M31 215H119" stroke="${hex(p.roofEdge)}" stroke-width="3"/>
    <circle cx="80" cy="82" r="25" fill="${hex(p.shade)}"/>
    <circle cx="80" cy="81" r="20" fill="${hex(p.primary)}"/>
    <path d="M66 81l14 -13 14 13 -14 13Z" fill="${hex(p.stone)}"/>
    <g fill="none" stroke="${hex(p.shade)}" stroke-width="3">${rays}${inscriptions}</g>
    <path d="M31 134H125" stroke="${hex(p.woodEdge)}" stroke-width="3"/>
    <path d="M116 10l-9 18 7 18 -16 14 M21 186l17 9 -7 21" fill="none" stroke="${hex(p.shade)}" stroke-width="3"/>
    <path d="M17 232H143L152 244H8Z" fill="${hex(p.roofEdge)}"/>
    <path d="M0 247H160V260H0Z" fill="${hex(p.stone)}"/>
    <path d="M16 251h47m19 0h57" stroke="${hex(p.primary)}" stroke-width="2"/>
  </g>`;
}

function attendant(p: TownPropPalette): string {
  return `<g data-part="temple-attendant">
    <path d="M43 84Q37 42 68 9L80 4Q116 40 111 88L125 116 116 169
      125 229 117 260 112 243 101 167 107 294 125 383H25L43 290 48 171
      35 245 26 264 16 248 23 185 19 148 30 139 25 122Z" fill="${hex(p.shade)}"/>
    <path d="M47 81Q44 42 75 14Q105 39 107 80L91 101H62Z" fill="${hex(p.primary)}"/>
    <path d="M53 76Q58 47 75 36Q94 52 100 77L88 95H64Z" fill="${hex(p.stone)}"/>
    <path d="M66 53L82 48 91 78 77 96 63 77Z" fill="${hex(p.shade)}"/>
    <path d="M72 57l10 1 3 22 -9 8 -7 -12Z" fill="${hex(p.stone)}"/>
    <path d="M42 107L61 99 87 102 108 111 96 133 51 131Z" fill="${hex(p.wood)}"/>
    <path d="M48 109L73 121 101 112" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="4"/>
    <path d="M43 137L72 129 105 138 92 223 105 300 116 376H34L52 291 57 220Z" fill="${hex(p.primary)}"/>
    <path d="M64 144L73 133 86 145 74 362 64 366 70 229Z M92 242L87 309 99 372H110Z" fill="${hex(p.stone)}"/>
    <path d="M48 288l-9 82 18 -5 7 -99 -7 -57Z" fill="${hex(p.shade)}"/>
    <path d="M32 136l14 10 -15 88 -9 13 9 -55 -6 -39Z
      M111 132l7 15 -9 19 11 63 -4 10 -18 -83Z" fill="${hex(p.stone)}"/>
    <path d="M93 189l-13 13 7 16 -9 17 M51 327l11 8 -3 21" fill="none" stroke="${hex(p.shade)}" stroke-width="3"/>
    <path d="M23 379H127V396H23Z" fill="${hex(p.roofEdge)}"/>
    <path d="M10 396H139L150 409V420H0V410Z" fill="${hex(p.stone)}"/>
    <path d="M15 399H134 M8 410H143" stroke="${hex(p.primary)}" stroke-width="3"/>
    <path d="M110 407l9 13h-20l3 -10Z" fill="${hex(p.shade)}"/>
  </g>`;
}

export function buildTempleLandmarkBody(template: TempleLandmarkTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'temple_sanctum_front': return sanctum(palette);
  case 'temple_inscribed_relief': return relief(palette);
  case 'temple_attendant_statue': return attendant(palette);
  }
}
